import { lazy, Suspense } from 'react';
import { hasStoredSupabaseSession } from '../../lib/sessionHint.js';

const loadLanding = () => import('./LandingPage.js');
const LandingPage = lazy(() => loadLanding().then((m) => ({ default: m.LandingPage })));
const AuthedApp = lazy(() => import('../../AuthedApp.js').then((m) => ({ default: m.AuthedApp })));

/**
 * Start fetching the landing chunk as soon as the entry evaluates (before
 * React mounts) for a visitor without a stored session. The resource hints
 * for its CSS, fonts and hero photo are injected into index.html at build
 * time by the `landingPreload` plugin (vite.config.ts), under the same
 * condition, so they start even earlier.
 */
function warmLanding(): void {
  if (hasStoredSupabaseSession()) return;
  void loadLanding();
}

if (typeof window !== 'undefined' && window.location.pathname === '/') warmLanding();

export function Loader({ label }: { label: string }): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-nt-bg text-nt-text-muted">
      <div className="flex items-center gap-3">
        <span className="h-3 w-3 rounded-full bg-nt-orange animate-pulse" />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}

/**
 * "/" — landing para visitantes, dashboard para quien tiene sesión.
 *
 * Decide en sincrónico, sin supabase-js: si no hay token guardado en
 * localStorage, renderiza la landing (su chunk + fotos + fuentes) y nada
 * más. Si hay token, carga el shell autenticado, que valida la sesión con
 * supabase-js y redirige a /dashboard (o, si el token venció, muestra la
 * landing desde AuthRoot).
 */
export function RootRoute(): JSX.Element {
  if (hasStoredSupabaseSession()) {
    return (
      <Suspense fallback={<Loader label="Cargando…" />}>
        <AuthedApp />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<div className="min-h-screen" style={{ background: '#050507' }} />}>
      <LandingPage />
    </Suspense>
  );
}
